import { useEffect, useState } from "react";
import { CheckCircle2, ImagePlus, Phone, X } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { site, telephoneHref } from "@/lib/site";

const projectTypes = [
  "Kitchen Demolition",
  "Bathroom Demolition",
  "Basement Demolition",
  "Flooring Removal",
  "Commercial Interior Demolition",
  "Other Interior Demolition",
] as const;

const estimateSchema = z.object({
  name: z.string().trim().min(1, "Your name is required").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  phone: z.string().trim().min(7, "Enter a valid phone number").max(30),
  preferredContact: z.enum(["Phone", "Text", "Email"]),
  bestContactTime: z.string().trim().min(1, "Choose the best time to reach you").max(80),
  projectType: z.enum(projectTypes),
  propertyType: z.enum(["Residential", "Commercial"]),
  address: z.string().trim().min(5, "Enter the project address").max(300),
  approximateArea: z.string().trim().max(80),
  scopeNotes: z.string().trim().min(10, "Tell us what needs to be removed").max(3000),
});

type EstimateForm = z.infer<typeof estimateSchema>;
type FormErrors = Partial<Record<keyof EstimateForm, string>>;

const initialForm: EstimateForm = {
  name: "",
  email: "",
  phone: "",
  preferredContact: "Phone",
  bestContactTime: "",
  projectType: "Kitchen Demolition",
  propertyType: "Residential",
  address: "",
  approximateArea: "",
  scopeNotes: "",
};

const BookEstimate = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState<EstimateForm>(initialForm);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const urls = photos.map((photo) => URL.createObjectURL(photo));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [photos]);

  const update = <K extends keyof EstimateForm>(key: K, value: EstimateForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const handlePhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(event.target.files ?? []);
    const valid = incoming.filter((file) => file.type.startsWith("image/") && file.size <= 10 * 1024 * 1024);
    if (valid.length !== incoming.length) {
      toast({ title: "Some photos were skipped", description: "Upload image files no larger than 10 MB each." });
    }
    setPhotos((current) => [...current, ...valid].slice(0, 8));
    event.target.value = "";
  };

  const uploadPhotos = async () => {
    const photoUrls: string[] = [];
    const uploadedPaths: string[] = [];
    const submissionId = crypto.randomUUID();
    for (const [index, file] of photos.entries()) {
      const safeExtension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      const path = `demolition-estimates/${submissionId}/${index}.${safeExtension}`;
      const { error: uploadError } = await supabase.storage.from("cleanout-photos").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (uploadError) throw uploadError;
      uploadedPaths.push(path);
      const { data, error: urlError } = await supabase.storage.from("cleanout-photos").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (urlError || !data?.signedUrl) throw urlError ?? new Error("Photo link could not be created");
      photoUrls.push(data.signedUrl);
    }
    return { photoUrls, uploadedPaths };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = estimateSchema.safeParse(form);
    if (!result.success) {
      const nextErrors: FormErrors = {};
      result.error.errors.forEach((error) => {
        const key = error.path[0] as keyof EstimateForm | undefined;
        if (key && !nextErrors[key]) nextErrors[key] = error.message;
      });
      setErrors(nextErrors);
      return;
    }

    setLoading(true);
    try {
      const { photoUrls, uploadedPaths } = await uploadPhotos();
      const { error } = await supabase.from("demolition_estimates").insert({
        name: result.data.name,
        email: result.data.email,
        phone: result.data.phone,
        preferred_contact: result.data.preferredContact,
        best_contact_time: result.data.bestContactTime,
        project_type: result.data.projectType,
        property_type: result.data.propertyType,
        address: result.data.address,
        approximate_area: result.data.approximateArea || null,
        scope_notes: result.data.scopeNotes,
        photo_urls: photoUrls,
      });
      if (error) {
        if (uploadedPaths.length > 0) await supabase.storage.from("cleanout-photos").remove(uploadedPaths);
        throw error;
      }
      navigate("/thank-you");
    } catch {
      toast({ title: "Your estimate request was not sent", description: `Please try again or call us at ${site.phone}.`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (key: keyof EstimateForm) => errors[key] ? <p className="mt-1 text-xs text-destructive" role="alert">{errors[key]}</p> : null;

  return (
    <>
      <section className="relative overflow-hidden bg-cover bg-center py-16 md:py-24" style={{ backgroundImage: "url('/images/chimney.jpg')" }}>
        <div className="absolute inset-0 bg-black/65" aria-hidden="true" />
        <div className="container relative z-10 max-w-3xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase text-white/85">Free project review</p>
          <h1 className="mb-4 text-3xl font-heading font-extrabold text-white md:text-5xl">Request Your Demolition Estimate</h1>
          <p className="mx-auto max-w-2xl text-lg text-white/90">Tell us what needs to come out and upload a few clear photos. We’ll review the scope and contact you the way you prefer.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container max-w-3xl">
          <Card>
            <CardContent className="p-6 md:p-9">
              <form onSubmit={handleSubmit} className="space-y-8" noValidate>
                <fieldset className="space-y-5">
                  <legend className="mb-5 text-xl font-heading font-bold">Contact information</legend>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div><Label htmlFor="name">Full name *</Label><Input id="name" autoComplete="name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5" aria-invalid={!!errors.name} />{fieldError("name")}</div>
                    <div><Label htmlFor="phone">Phone *</Label><Input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5" aria-invalid={!!errors.phone} />{fieldError("phone")}</div>
                  </div>
                  <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" aria-invalid={!!errors.email} />{fieldError("email")}</div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div><Label>Preferred contact *</Label><Select value={form.preferredContact} onValueChange={(value: EstimateForm["preferredContact"]) => update("preferredContact", value)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Phone">Phone call</SelectItem><SelectItem value="Text">Text message</SelectItem><SelectItem value="Email">Email</SelectItem></SelectContent></Select></div>
                    <div><Label>Best time to reach you *</Label><Select value={form.bestContactTime} onValueChange={(value) => update("bestContactTime", value)}><SelectTrigger className="mt-1.5" aria-invalid={!!errors.bestContactTime}><SelectValue placeholder="Choose a time" /></SelectTrigger><SelectContent><SelectItem value="Morning">Morning</SelectItem><SelectItem value="Afternoon">Afternoon</SelectItem><SelectItem value="Evening">Evening</SelectItem><SelectItem value="Anytime">Anytime</SelectItem></SelectContent></Select>{fieldError("bestContactTime")}</div>
                  </div>
                </fieldset>

                <fieldset className="space-y-5 border-t pt-8">
                  <legend className="mb-5 text-xl font-heading font-bold">Project details</legend>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div><Label>Project type *</Label><Select value={form.projectType} onValueChange={(value: EstimateForm["projectType"]) => update("projectType", value)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{projectTypes.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Property type *</Label><Select value={form.propertyType} onValueChange={(value: EstimateForm["propertyType"]) => update("propertyType", value)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Residential">Residential</SelectItem><SelectItem value="Commercial">Commercial</SelectItem></SelectContent></Select></div>
                  </div>
                  <div><Label htmlFor="address">Project address *</Label><Input id="address" autoComplete="street-address" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Street, city, and ZIP code" className="mt-1.5" aria-invalid={!!errors.address} />{fieldError("address")}</div>
                  <div><Label htmlFor="area">Approximate project area</Label><Input id="area" value={form.approximateArea} onChange={(e) => update("approximateArea", e.target.value)} placeholder="Example: 250 sq. ft. kitchen or 1,200 sq. ft. office" className="mt-1.5" /></div>
                  <div><Label htmlFor="scope">What needs to be removed? *</Label><Textarea id="scope" value={form.scopeNotes} onChange={(e) => update("scopeNotes", e.target.value)} placeholder="Describe cabinets, tile, flooring, drywall, ceilings, fixtures, access, materials that must stay, and your ideal timing." rows={6} className="mt-1.5" aria-invalid={!!errors.scopeNotes} />{fieldError("scopeNotes")}</div>
                </fieldset>

                <fieldset className="space-y-4 border-t pt-8">
                  <legend className="mb-4 text-xl font-heading font-bold">Project photos</legend>
                  <p className="text-sm text-muted-foreground">Upload up to eight clear images showing the full work area, materials, and access route. Maximum 10 MB each.</p>
                  <Label htmlFor="photos" className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-muted/40 text-center transition-colors hover:bg-muted">
                    <ImagePlus className="mb-2 h-6 w-6 text-primary" />
                    <span className="font-semibold">Add project photos</span>
                    <span className="text-xs text-muted-foreground">JPG, PNG, WEBP, or HEIC</span>
                  </Label>
                  <Input id="photos" type="file" accept="image/*" multiple onChange={handlePhotos} className="sr-only" />
                  {previews.length > 0 && <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{previews.map((src, index) => <div key={src} className="relative aspect-square overflow-hidden rounded-md border"><img src={src} alt={`Selected project photo ${index + 1}`} className="h-full w-full object-cover" /><Button type="button" variant="destructive" size="icon" aria-label={`Remove photo ${index + 1}`} onClick={() => setPhotos((current) => current.filter((_, photoIndex) => photoIndex !== index))} className="absolute right-1 top-1 h-7 w-7"><X className="h-4 w-4" /></Button></div>)}</div>}
                </fieldset>

                <Button type="submit" size="lg" className="w-full" disabled={loading}>{loading ? "Sending estimate request…" : "Request My Demolition Estimate"}</Button>
                <p className="text-center text-xs text-muted-foreground">No obligation. Prefer to talk now? <a href={telephoneHref} className="font-semibold text-primary hover:underline">Call {site.phone}</a>.</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
};

export default BookEstimate;
